import type { ModuleFederationRuntimePlugin } from '@module-federation/runtime';

/**
 * Lets the host boot when MFE dev servers are not running.
 * Without this plugin, a dev server crashes the host before any route renders.
 */
const mfOfflineFallback = (): ModuleFederationRuntimePlugin => ({
  name: 'mf-offline-fallback',
  async errorLoadRemote(args) {
    if (process.env.PUBLIC_STAGE !== 'LOCAL') {
      return undefined;
    }

    const { lifecycle, id } = args;
    const remoteId = id || 'offline';

    if (lifecycle === 'afterResolve') {
      return {
        id: remoteId,
        name: remoteId,
        metaData: {
          name: remoteId,
          type: 'app',
          buildInfo: { buildVersion: 'offline', buildName: 'offline' },
          remoteEntry: {
            name: 'data:text/javascript,',
            path: '',
            type: 'global',
          },
          types: { path: '', name: '', zip: '', api: '' },
          globalName: remoteId,
          pluginVersion: '0.0.0',
          publicPath: '',
        },
        shared: [],
        remotes: [],
        exposes: [],
      } as unknown;
    }

    if (lifecycle === 'onLoad') {
      const message = `MFE "${remoteId}" is not running locally. Start its dev server to use this route.`;
      console.warn(`[mf-offline-fallback] ${message}`);

      // NOTE: Do not import shared dependencies (react, mui, etc.) at module
      // scope. This file is bundled into the host's entry chunk and runs before
      // the Federation Runtime installs share-consume handlers - top-level
      // imports of shared modules would crash with "factory is undefined".
      const React = await import('react');
      const StubComponent = () =>
        React.createElement(
          'div',
          { style: { padding: 16, fontFamily: 'monospace' } },
          message,
        );

      return () => ({
        __esModule: true,
        default: StubComponent,
      });
    }

    return undefined;
  },
});

export default mfOfflineFallback;
